import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { RouterLink, RouterOutlet } from '@angular/router'

import { NgxLoadingBar } from '@ngx-loading-bar/core'
import { TranslateService } from '@ngx-translate/core'
import { PrimeTemplate, ToastMessageOptions } from 'primeng/api'
import { PrimeNG } from 'primeng/config'
import { Toast } from 'primeng/toast'

import { AuthSession } from '@ti/core/auth/services'
import { DeviceService, TitleController } from '@ti/core/common/services'
import { BrandingService } from '@ti/core/common/services/branding.service'
import { injectDestroy$ } from '@ti/core/common/utils'
import { SCREEN_FACADE } from '@ti/core/contracts/screen.facade'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { AppUpdateComponent } from '@ti/core/ui-kit/layout/components/app-update/app-update.component'
import { LandscapeModeOverlayComponent } from '@ti/core/ui-kit/layout/components/landscape-mode-overlay/landscape-mode-overlay.component'
import { applyPrimeNgOverrides } from '@ti/core/ui-kit/prime-ng/overrides'
import { AlertNotificationsHostComponent } from '@ti/shared/alerts/components/notifications/notification-host.component'
import { ChatNotifHostComponent } from '@ti/shared/chat/components/chat-notif-host/chat-notif-host.component'
import { BookmarkSharedNotifHostComponent } from '@ti/fleet/bookmarks/components/bookmark-shared-notif-host/bookmark-shared-notif-host.component'

// TODO: Implement better way to apply overrides
applyPrimeNgOverrides()

@Component({
  selector: 'app-fleet-root',
  templateUrl: 'fleet-app.component.html',
  styleUrl: 'fleet-app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertNotificationsHostComponent,
    AppUpdateComponent,
    BookmarkSharedNotifHostComponent,
    ChatNotifHostComponent,
    LandscapeModeOverlayComponent,
    NgClass,
    NgxLoadingBar,
    PrimeTemplate,
    RouterLink,
    RouterOutlet,
    Toast,
  ],
})
export class FleetAppComponent implements OnInit {
  protected readonly authSession = inject(AuthSession)
  protected readonly branding = inject(BrandingService)
  protected readonly title = inject(TitleController)
  protected readonly deviceService = inject(DeviceService)
  protected readonly translate = inject(TranslateService)
  protected readonly primeNgConfig = inject(PrimeNG)
  protected readonly languageService = inject(LanguageService)
  protected readonly screen = inject(SCREEN_FACADE)
  protected readonly destroy$ = injectDestroy$()

  protected readonly isNativePlatform = this.deviceService.getPlatform() !== 'web'

  public async ngOnInit() {
    this.configurePrimeNg()
    this.title.subscribe()
    this.applyBranding()
    await this.attemptLockingOrientation()
  }

  protected applyBranding() {
    this.branding.applyFavicon()
    this.branding.applyBranding()
  }

  // The message template gets no close callback, so a toast link removes its own message
  protected closeToast(toast: Toast, message: ToastMessageOptions) {
    const index = toast.messages?.indexOf(message) ?? -1
    if (index >= 0) toast.onMessageClose({ index, message })
  }

  protected runToastAction(action: () => void, toast: Toast, message: ToastMessageOptions) {
    action()
    this.closeToast(toast, message)
  }

  protected configurePrimeNg() {
    this.primeNgConfig.ripple.set(true)
  }

  protected async attemptLockingOrientation() {
    if (this.deviceService.isMobile()) {
      await this.screen.lockOrientation('portrait')
    }
  }
}
